import type { AttendanceRecord, EmployeeSummary } from '../types/models';

export const getLocalDateString = (): string => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
};

export const getDateStringFromDate = (date: Date): string => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
};

export const buildRecentDateStrings = (referenceDate: Date, days: number): string[] => {
  const safeDays = Math.max(0, Math.floor(days));
  if (safeDays === 0) return [];
  const base = new Date(referenceDate);
  base.setHours(0, 0, 0, 0);
  const results: string[] = [];
  const seen = new Set<string>();
  for (let offset = 0; offset < safeDays; offset += 1) {
    const date = new Date(base);
    date.setDate(base.getDate() - offset);
    const iso = getDateStringFromDate(date);
    if (!seen.has(iso)) {
      seen.add(iso);
      results.push(iso);
    }
  }
  return results;
};

export const buildRecordKeyCandidates = (employee: EmployeeSummary | undefined, date: string): string[] => {
  if (!employee) return [];
  const keys: string[] = [];
  if (employee.employeeCode) {
    keys.push(String(employee.employeeCode));
  }
  if (employee.id && employee.id !== employee.employeeCode) {
    keys.push(String(employee.id));
  }
  return keys.map((key) => `${key}-${date}`);
};

export const findRecordByKeys = (
  keys: string[],
  records: Record<string, AttendanceRecord>
): { key: string; record: AttendanceRecord } | null => {
  for (const key of keys) {
    const record = records[key];
    if (record) {
      return { key, record };
    }
  }
  return null;
};

export const findOpenRecordByKeys = (
  keys: string[],
  records: Record<string, AttendanceRecord>
): { key: string; record: AttendanceRecord } | null => {
  for (const key of keys) {
    const record = records[key];
    if (record?.clockIn && !record?.clockOut) {
      return { key, record };
    }
  }
  return null;
};

export const findOpenRecordByDates = (
  employee: EmployeeSummary | undefined,
  dateStrings: string[],
  records: Record<string, AttendanceRecord>
): { key: string; record: AttendanceRecord } | null => {
  if (!employee) return null;
  for (const date of dateStrings) {
    const keys = buildRecordKeyCandidates(employee, date);
    const openRecord = findOpenRecordByKeys(keys, records);
    if (openRecord) {
      return openRecord;
    }
  }
  return null;
};

export const resolveShiftTypeForPunchOut = (
  clockInDate: Date,
  clockOutDate: Date
): 'day' | 'night' => {
  const clockInIso = getDateStringFromDate(clockInDate);
  const clockOutIso = getDateStringFromDate(clockOutDate);
  if (clockOutIso <= clockInIso) {
    return 'day';
  }
  return clockInDate.getHours() >= 16 ? 'night' : 'day';
};
